import { type Plan } from "@langfuse/shared";

// Entitlements: Binary feature access
// Exported to silence @typescript-eslint/no-unused-vars v8 warning
// (used for type extraction via typeof, which is a legitimate pattern)
export const entitlements = [
  // features
  "rbac-project-roles",
  "cloud-billing",
  "cloud-spend-alerts",
  "cloud-multi-tenant-sso",
  "self-host-ui-customization",
  "self-host-allowed-organization-creators",
  "trace-deletion", // Not in use anymore, but necessary to use the TableAction type.
  "audit-logs",
  "data-retention",
  "scheduled-blob-exports",
  "prompt-protected-labels",
  "admin-api",
  "in-app-agent",
] as const;
export type Entitlement = (typeof entitlements)[number];

const cloudAllPlansEntitlements: Entitlement[] = [
  "cloud-billing",
  "trace-deletion",
  "in-app-agent",
];

const selfHostedAllPlansEntitlements: Entitlement[] = [
  "trace-deletion",
  "scheduled-blob-exports",
  // ScaleSail isolates business data by Langfuse project. Project roles are
  // therefore a baseline authorization capability for every self-hosted plan,
  // while cloud plan entitlements remain unchanged.
  "rbac-project-roles",
  // Organization API keys are the Hub-style prompt catalog credential on this
  // self-hosted instance. Cloud still gates the same UI behind paid plans.
  "admin-api",
];

// Entitlement Limits: Limits on the number of resources that can be created/used
// Exported to silence @typescript-eslint/no-unused-vars v8 warning
// (used for type extraction via typeof, which is a legitimate pattern)
export const entitlementLimits = [
  "annotation-queue-count",
  "organization-member-count",
  "data-access-days",
  "model-based-evaluations-count-evaluators",
  "prompt-management-count-prompts",
  "monitor-count",
] as const;
export type EntitlementLimit = (typeof entitlementLimits)[number];

export type EntitlementLimits = Record<
  EntitlementLimit,
  | number // if limited
  | false // unlimited
>;

export const entitlementAccess: Record<
  Plan,
  {
    entitlements: Entitlement[];
    entitlementLimits: EntitlementLimits;
  }
> = {
  "cloud:hobby": {
    entitlements: [...cloudAllPlansEntitlements],
    entitlementLimits: {
      "organization-member-count": 2,
      "data-access-days": 30,
      "annotation-queue-count": 1,
      "model-based-evaluations-count-evaluators": false,
      "prompt-management-count-prompts": false,
      "monitor-count": 2,
    },
  },
  "cloud:core": {
    entitlements: [...cloudAllPlansEntitlements, "cloud-spend-alerts"],
    entitlementLimits: {
      "organization-member-count": false,
      "data-access-days": 90,
      "annotation-queue-count": 3,
      "model-based-evaluations-count-evaluators": false,
      "prompt-management-count-prompts": false,
      "monitor-count": 20,
    },
  },
  "cloud:pro": {
    entitlements: [
      ...cloudAllPlansEntitlements,
      "cloud-spend-alerts",
      "data-retention",
    ],
    entitlementLimits: {
      "annotation-queue-count": false,
      "organization-member-count": false,
      "data-access-days": false,
      "model-based-evaluations-count-evaluators": false,
      "prompt-management-count-prompts": false,
      "monitor-count": 50,
    },
  },
  "cloud:team": {
    entitlements: [
      ...cloudAllPlansEntitlements,
      "rbac-project-roles",
      "audit-logs",
      "data-retention",
      "cloud-multi-tenant-sso",
      "prompt-protected-labels",
      "admin-api",
      "scheduled-blob-exports",
      "cloud-spend-alerts",
    ],
    entitlementLimits: {
      "annotation-queue-count": false,
      "organization-member-count": false,
      "data-access-days": false,
      "model-based-evaluations-count-evaluators": false,
      "prompt-management-count-prompts": false,
      "monitor-count": 50,
    },
  },
  "cloud:enterprise": {
    entitlements: [
      ...cloudAllPlansEntitlements,
      "rbac-project-roles",
      "audit-logs",
      "data-retention",
      "cloud-multi-tenant-sso",
      "prompt-protected-labels",
      "admin-api",
      "scheduled-blob-exports",
      "cloud-spend-alerts",
    ],
    entitlementLimits: {
      "annotation-queue-count": false,
      "organization-member-count": false,
      "data-access-days": false,
      "model-based-evaluations-count-evaluators": false,
      "prompt-management-count-prompts": false,
      "monitor-count": 100,
    },
  },
  oss: {
    entitlements: selfHostedAllPlansEntitlements,
    entitlementLimits: {
      "annotation-queue-count": false,
      "organization-member-count": false,
      "data-access-days": false,
      "model-based-evaluations-count-evaluators": false,
      "prompt-management-count-prompts": false,
      "monitor-count": false,
    },
  },
  "self-hosted:pro": {
    entitlements: selfHostedAllPlansEntitlements,
    entitlementLimits: {
      "annotation-queue-count": false,
      "organization-member-count": false,
      "data-access-days": false,
      "model-based-evaluations-count-evaluators": false,
      "prompt-management-count-prompts": false,
      "monitor-count": false,
    },
  },
  "self-hosted:enterprise": {
    entitlements: [
      ...selfHostedAllPlansEntitlements,
      "rbac-project-roles",
      "self-host-allowed-organization-creators",
      "self-host-ui-customization",
      "audit-logs",
      "data-retention",
      "prompt-protected-labels",
    ],
    entitlementLimits: {
      "annotation-queue-count": false,
      "organization-member-count": false,
      "data-access-days": false,
      "model-based-evaluations-count-evaluators": false,
      "prompt-management-count-prompts": false,
      "monitor-count": false,
    },
  },
};
